import { useFaceDetections } from '@src/hooks/useFaceDetections';
import { buildSpeakerLabelToFaceLabelsMap } from '@src/services/framing/speakerRanking';
import { useStore } from '@src/stores-v2/StoreContext';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { fetchSpeakerVoiceMappings } from '@src/utility/speakerUtils';
import { useEffect, useState } from 'react';

const logger = LogManager.getLogger('hook:useSpeakerFaceMapping');

/**
 * Face detections plus the composite transcript `speaker_label` → `face_label`
 * map that active-speaker framing needs. Resolving it takes three sources —
 * voice mappings, face identifications and face tracks — and every surface that
 * frames by speaker (Creator Studio's Framing tab, the Content Studio reframe
 * modal) has to combine them the same way, so it lives here.
 *
 * Pass `storyId: undefined` to skip all fetching (e.g. when the calling surface
 * is feature-flagged off).
 */
export function useSpeakerFaceMapping(
  storyId: string | undefined,
  enablePolling: boolean = false,
) {
  const { speakerFaceMapStore } = useStore();
  const faceDetectionsState = useFaceDetections(storyId, enablePolling);
  const { speakerIdentifications, faceTracks } = faceDetectionsState;

  const [voiceMappings, setVoiceMappings] = useState<Record<string, string>>(
    {},
  );
  const [voiceMappingsLoading, setVoiceMappingsLoading] = useState(false);
  const [speakerLabelToFaceLabels, setSpeakerLabelToFaceLabels] = useState<
    Record<string, string[]>
  >({});

  // Fetch voice mappings once per story; shared with the face-mapping modal.
  useEffect(() => {
    if (!storyId) {
      setVoiceMappings({});
      return;
    }

    let cancelled = false;
    setVoiceMappingsLoading(true);
    fetchSpeakerVoiceMappings(storyId)
      .then(result => {
        if (cancelled) return;
        setVoiceMappings(result?.mappings ?? {});
      })
      .catch(error => {
        if (cancelled) return;
        logger.warn(
          'Failed to fetch speaker voice mappings',
          extractErrorInfo(error),
        );
        setVoiceMappings({});
      })
      .finally(() => {
        if (cancelled) return;
        setVoiceMappingsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [storyId]);

  // Build speaker_label → face_label once all three sources are populated.
  useEffect(() => {
    if (
      !speakerIdentifications ||
      Object.keys(speakerIdentifications).length === 0 ||
      Object.keys(voiceMappings).length === 0 ||
      !faceTracks ||
      Object.keys(faceTracks).length === 0
    ) {
      setSpeakerLabelToFaceLabels({});
      return;
    }
    const map = buildSpeakerLabelToFaceLabelsMap(
      voiceMappings,
      speakerIdentifications,
      faceTracks,
    );
    setSpeakerLabelToFaceLabels(map);
    // Keep the story-level copy current (e.g. after a face remap) so scenes
    // created mid-edit frame by the same mapping.
    if (storyId) speakerFaceMapStore.set(storyId, map);
  }, [
    speakerIdentifications,
    voiceMappings,
    faceTracks,
    storyId,
    speakerFaceMapStore,
  ]);

  return {
    ...faceDetectionsState,
    voiceMappings,
    speakerLabelToFaceLabels,
    /** True while either source of the mapping is still in flight. */
    isMappingLoading: faceDetectionsState.loading || voiceMappingsLoading,
  };
}
